import { useEffect, useState } from "react";
import moment from "moment";
import useUser from "../../core/user/useUser";
import { Row, Spacer } from "../../components/Layout/Layout";
import useTranslations from "../../core/i18n/useTranslations";
import { patchUpdateObject } from "../../core/api/objects/objects";
import { ObjectPatch } from "../../core/api/objects/types";
import { notify } from "../../ui-lib/components/Alerts/Toast";
import { RowData } from "../../pages/Objects/ObjectsTable";
import { IDropdownItem } from "../../ui-lib/components/Dropdown/DropdownItem";
import { getSubscriptions, Subscription } from "../../core/api/subscriptions";
import Dropdown from "../../ui-lib/components/Dropdown/Dropdown";
import DatePicker from "../../ui-lib/components/DateAndTime/DatePicker";
import { notifyApiErrors } from "../../core/helpers/helpers";
import PrimeModal from "../../ui-lib/components/PrimeModal/PrimeModal";

const ChangeSubscriptionModal = ({
  onClose,
  onSuccess,
  modalTitle,
  text,
  objects,
  isOpen,
  responseCenterId,
}: {
  onClose?: () => void;
  onSuccess?: () => void;
  modalTitle: string;
  text: string;
  objects: RowData[];
  isOpen: boolean;
  responseCenterId: number;
}) => {
  const t = useTranslations();
  const { authenticatedRequest, dateFormat } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<IDropdownItem[]>([]);
  const [selectedSubscription, setSelectedSubscription] =
    useState<IDropdownItem>();
  const [selectedEndDate, setSelectedEndDate] = useState<Date>();

  const close = () => {
    setSelectedSubscription(undefined);
    setSelectedEndDate(undefined);
    onClose?.();
  };

  const handleSubmit = async () => {
    let success = true;
    if (selectedSubscription !== undefined && selectedEndDate !== undefined) {
      setIsLoading(true);
      const pairs = objects.map((sr) => ({
        ObjectId: sr.id,
        ObjectName: sr.Identifier,
      }));
      const object: ObjectPatch = {
        nextSubscriptionId: +selectedSubscription.id,
        responseCenterValidTo: moment(selectedEndDate)
          .format("YYYY-MM-DD")
          .toString(),
      };
      await Promise.all(
        pairs.map(async (pair) => {
          try {
            await patchUpdateObject(
              pair.ObjectId,
              object,
              authenticatedRequest,
            );
          } catch (error: any) {
            success = false;
            if (error.response?.data?.Message) {
              notify({
                message: t(error.response.data.Message),
              });
            } else {
              notifyApiErrors(
                error.response?.data?.errors,
                error.response?.status,
              );
            }
          }
        }),
      );
    }
    setIsLoading(false);
    if (success) {
      notify({
        message: t("Objects:change_subscription_success"),
      });
      onSuccess?.();
      close();
    }
  };
  useEffect(() => {
    getSubscriptions(responseCenterId, authenticatedRequest)
      .then((subs: Subscription[]) => {
        if (subs !== undefined) {
          const subscriptionDropdownItems: IDropdownItem[] = [];
          subs.forEach((sub) => {
            const subName = subs.length === 1 ? "" : ` (${sub.description})`;
            sub.subscriptionPeriods?.forEach((period) => {
              subscriptionDropdownItems.push({
                id: period.subscriptionId,
                name: `${t("Objects:subscription_activatefor")} ${
                  period.period
                } ${t("Objects:subscription_months")}${subName}`,
                isSelected: false,
              });
            });
          });
          setSubscriptions(subscriptionDropdownItems);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <PrimeModal
      withFooter
      withHeader
      loading={isLoading}
      onClose={() => {
        close();
      }}
      isOpen={isOpen}
      header={modalTitle}
      fixedWidth="576px"
      submitBtn={{
        text: `${t("Objects:button_change_subscription")} (${objects.length})`,
        onClick: handleSubmit,
      }}
      cancelBtn={{ text: t("Common:cancel"), onClick: close }}
    >
      <p className="grey-600">
        {text.replace("{objects}", objects.length.toString())}
      </p>
      <Spacer size={16} />
      {subscriptions && (
        <Row>
          <Dropdown
            title={t("Objects:label_change_subscription")}
            id="change_subscription"
            items={subscriptions}
            disabled={subscriptions === undefined || subscriptions.length === 0}
            selectedItem={subscriptions.find((r) => r.isSelected)}
            onSelectItem={(item) => {
              subscriptions.forEach((r) => {
                r.isSelected = false;
              });
              item.isSelected = true;
            }}
          />
          <Spacer />
          <DatePicker
            label={t("Objects:label_subscription_ends")}
            dateFormat={dateFormat.split("/").join("-")}
            placeholder={t("Objects:placeholder_subscription_ends")}
            value={selectedEndDate}
            onChange={(newDate) => {
              setSelectedEndDate(newDate || undefined);
            }}
          />
        </Row>
      )}
    </PrimeModal>
  );
};

export default ChangeSubscriptionModal;
